import React, { useMemo, useState } from 'react';
import { Box, Button, Card, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import * as Yup from 'yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import UserService from '@api/Users/UserCtrl';
import { PersonalDataType, UpdatePinType } from '@api/Users/UserServ';
import { showAlertMessage } from '@utils/Alerts/Message';
import { setLoggedUser } from '@store/authStore';
import { getBase64FromInputFile } from '@utils/Base64';
import OnlineAvatar from '@components/Utilities/Avatars/OnlineAvatar';
import { ModalChangeEmail } from '@components/Admin/Users/Modals/ModalChangeEmail';

const updateProfileSchema = Yup.object({
    firstname: Yup.string().required('Nombre requerido'),
    lastname: Yup.string().required('Apellido requerido'),
    email: Yup.string().required('Correo requerido'),
    address: Yup.string().required('Direccíon requerido'),
    phone: Yup.string().required('Teléfono requerido')
});

const updatePINSchema = Yup.object({
    oldPin: Yup.string().required('Pin actual requerido'),
    newPin: Yup.string()
        .min(6, 'El pin debe de ser de 6 digitos')
        .max(6, 'El pin debe de ser de 6 digitos')
        .required('Nuevo pin requerido'),
    confirmNewPin: Yup.string().oneOf([Yup.ref('newPin')], 'Pin debe coincidir')
});
const MyProfileScreen: React.FunctionComponent = () => {
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const userService = useMemo(() => new UserService(), []);

    const dispatch = useDispatch();
    const [changeEmailOpen, setChangeEmailOpen] = useState(false);

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<PersonalDataType>({
        resolver: yupResolver(updateProfileSchema)
    });

    const pinForm = useForm<UpdatePinType>({
        resolver: yupResolver(updatePINSchema)
    });
    const onUpdatePersonalData: SubmitHandler<PersonalDataType> = async (values) => {
        showLoader();
        try {
            const newUser = await userService.updatePersonalData(values);
            dispatch(setLoggedUser(newUser));
            showAlertMessage('Información personal actualizada exitosamente');
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onUpdatePin: SubmitHandler<UpdatePinType> = async (values) => {
        showLoader();
        try {
            await userService.updatePin(values);
            showAlertMessage('PIN actualizado exitosamente');
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const changeImage = async (event: any) => {
        showLoader();
        try {
            const base64File = await getBase64FromInputFile(event);
            const url = await userService.uploadProfilePhoto(base64File);
            if (loggedUser) {
                // Objeto nuevo: asignarle al `loggedUser` del store LANZA (Redux lo
                // congela). Hasta el 2026-09-27 la foto se subía y la pantalla
                // mostraba el error sin actualizar el avatar.
                dispatch(setLoggedUser({ ...loggedUser, profile: { ...loggedUser.profile, photo: url } }));
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    if (!loggedUser) {
        return <Skeleton />;
    }

    return (
        <Stack p={4} spacing={4}>
            <Box component={Card} boxShadow={4} borderRadius={4} p={4}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h5" fontWeight={600}>
                            Editar información personal
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Stack spacing={2} alignItems="center">
                            <OnlineAvatar
                                alt={loggedUser?.profile.name}
                                sx={{ width: 150, height: 150, border: '10px solid lightgray' }}
                                src={loggedUser.profile.photo}
                            />
                            <Box>
                                <Button component="label">
                                    Cambiar foto
                                    <input type="file" hidden onChange={changeImage} />
                                </Button>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <form id="update-personal-data-form" onSubmit={handleSubmit(onUpdatePersonalData)}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="firstname"
                                        control={control}
                                        defaultValue={loggedUser.profile.firstname}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Nombre"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.firstname}
                                                helperText={errors.firstname && errors.firstname.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="email"
                                        control={control}
                                        defaultValue={loggedUser.email}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                disabled
                                                fullWidth
                                                required
                                                label="Correo"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.email}
                                                helperText={errors.email && errors.email.message}
                                            />
                                        )}
                                    />
                                    <Button size="small" onClick={() => setChangeEmailOpen(true)}>
                                        Cambiar correo
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="address"
                                        control={control}
                                        defaultValue={loggedUser.profile.address}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Dirección"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.address}
                                                helperText={errors.address && errors.address.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="lastname"
                                        control={control}
                                        defaultValue={loggedUser.profile.lastname}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Apellido"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.lastname}
                                                helperText={errors.lastname && errors.lastname.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        defaultValue={loggedUser.profile.phone}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Número de teléfono"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.phone}
                                                helperText={errors.phone && errors.phone.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} textAlign="right">
                                    <Button variant="contained" type="submit" form="update-personal-data-form">
                                        Guardar datos
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                        <form id="update-pin-form" onSubmit={pinForm.handleSubmit(onUpdatePin)}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <Stack>
                                        <Typography variant="h5">Actualizar PIN</Typography>
                                        <Typography variant="body2">
                                            Tu PIN es tu clave de acceso junto con tu correo al sistema de KRISPIS®,
                                            actualizalo regularmente y no lo compartas Tu PIN debe ser de 6 digitos.
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={false} md={6} />
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="oldPin"
                                        control={pinForm.control}
                                        defaultValue=""
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Pin Actual"
                                                value={value}
                                                onChange={onChange}
                                                error={!!pinForm.formState.errors.oldPin}
                                                helperText={
                                                    pinForm.formState.errors.oldPin &&
                                                    pinForm.formState.errors.oldPin.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="newPin"
                                        control={pinForm.control}
                                        defaultValue=""
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Nuevo PIN"
                                                value={value}
                                                onChange={onChange}
                                                error={!!pinForm.formState.errors.newPin}
                                                helperText={
                                                    pinForm.formState.errors.newPin &&
                                                    pinForm.formState.errors.newPin.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="confirmNewPin"
                                        control={pinForm.control}
                                        defaultValue=""
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Confirmar nuevo PIN"
                                                value={value}
                                                onChange={onChange}
                                                error={!!pinForm.formState.errors.confirmNewPin}
                                                helperText={
                                                    pinForm.formState.errors.confirmNewPin &&
                                                    pinForm.formState.errors.confirmNewPin.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} textAlign="right">
                                    <Button variant="contained" type="submit" form="update-pin-form">
                                        Actualizar contraseña
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Grid>
                </Grid>
            </Box>
            {loggedUser && (
                <ModalChangeEmail
                    open={changeEmailOpen}
                    onClose={() => setChangeEmailOpen(false)}
                    user={loggedUser}
                    onChanged={(email) => {
                        setValue('email', email);
                        // Objeto nuevo: el `loggedUser` del store está congelado.
                        dispatch(setLoggedUser({ ...loggedUser, email }));
                    }}
                />
            )}
        </Stack>
    );
};
export default MyProfileScreen;
